import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HelpTrigger } from '../../../help';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, GitMerge, Plus, Upload } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import AdminModal from '../../components/AdminModal';
import BulkActionsBar from '../../components/BulkActionsBar';
import MultiSelectFilter, { MultiSelectOption } from '../../components/MultiSelectFilter';
import DuplicatesReviewModal from './DuplicatesReviewModal';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import { OFFICE_OPTIONS } from '../../../constants/candidates';
import AdminPageShell from '../../components/AdminPageShell';
import AdminListToolbar from '../../components/AdminListToolbar';
import ListPager from '../../components/ListPager';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';
import { formatPartyDisplay } from '../../../utils/partyUtils';
import { PeopleIcon } from '../../../components/icons';

/** Row from the person-grouped registry (GET /admin/candidates, registry_type=all). */
interface PersonRow {
  person_id: string;
  entity_kind: 'candidate' | 'legislator' | 'officeholder' | 'both';
  /** Latest candidacy id — the row's editable candidate status belongs to it. */
  candidate_id?: string | null;
  full_name: string;
  photo_url?: string;
  office_sought: string;
  district?: string;
  district_display?: string;
  party_affiliation?: string;
  candidate_status?: string | null;
  legislator_status?: string | null;
  officeholder_status?: string | null;
  status: string;
  candidate_latest_year?: number | null;
  legislator_latest_session_year?: number | null;
  possible_duplicate_count?: number;
}

const parsePeople = (raw: unknown): AdminListResult<PersonRow> => {
  const data = raw as { data?: PersonRow[]; pagination?: { total?: number; limit?: number; offset?: number } };
  const total = data.pagination?.total ?? 0;
  const limit = data.pagination?.limit ?? 0;
  const offset = data.pagination?.offset ?? 0;
  return { rows: data.data ?? [], total, hasMore: offset + limit < total };
};

const roleChip = (label: string, status: string | null | undefined, tone: string) => (
  <span key={label} className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
    {label}
    {status ? <span className="ml-1 opacity-75">· {status}</span> : null}
  </span>
);

const filterSelectClasses =
  'h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';

const COLUMN_COUNT = 8;

/**
 * Normalized district labels from /candidates/filter-options — the same
 * canonical values the registry's district column (and filter) uses. The
 * catalog is small and stable, so it's fetched once per session and searched
 * client-side.
 */
let districtOptionsCache: MultiSelectOption[] | null = null;
const fetchDistrictOptions = async (term: string): Promise<MultiSelectOption[]> => {
  if (!districtOptionsCache) {
    const response = await api.get('/candidates/filter-options');
    const rows = (response.data.data?.districts ?? []) as Array<{ district: string }>;
    districtOptionsCache = [...new Set(rows.map((row) => row.district))].map((district) => ({
      id: district,
      label: district,
    }));
  }
  const needle = term.toLowerCase();
  return needle
    ? districtOptionsCache.filter((option) => option.label.toLowerCase().includes(needle))
    : districtOptionsCache;
};

const fetchRoleStatusValues = async (roleType: string): Promise<string[]> => {
  const response = await api.get('/role-statuses', { params: { role_type: roleType } });
  const rows = (response.data.data ?? response.data ?? []) as Array<{ value: string }>;
  return rows.map((row) => row.value);
};

/**
 * People — person-centric list replacing the legacy Candidate/Legislator
 * Management hub. One row = one person; roles shown as badges with per-role
 * status. P2 added Add Person and native editing on the detail page; P3 adds
 * duplicate review & merge, election-results reconciliation (as a role
 * transition), and FEC import (Documentation/PEOPLE_TOOL_PLAN.md).
 */
const EMPTY_PERSON_FORM = { first_name: '', last_name: '', email: '', phone: '' };

const PeoplePage: React.FC = () => {
  const navigate = useNavigate();
  const [roleType, setRoleType] = useState('all');
  const [status, setStatus] = useState('');
  const [office, setOffice] = useState('');
  const [districts, setDistricts] = useState<string[]>([]);
  const [year, setYear] = useState('');

  const candidateStatuses = useQuery({
    queryKey: ['role-statuses', 'candidate'],
    queryFn: () => fetchRoleStatusValues('candidate'),
    staleTime: 5 * 60 * 1000,
  });
  const officeholderStatuses = useQuery({
    queryKey: ['role-statuses', 'officeholder'],
    queryFn: () => fetchRoleStatusValues('officeholder'),
    staleTime: 5 * 60 * 1000,
  });

  // The year filter offers configured election cycles, not free numbers
  // (ELECTIONS_TOOL_PLAN.md E2).
  const cycleYears = useQuery({
    queryKey: ['admin-elections', 'cycle-years', 'CO'],
    queryFn: async () => {
      const response = await api.get('/election-calendars', { params: { state: 'CO' } });
      const rows = (response.data.data ?? []) as Array<{ election_year: number }>;
      return [...new Set(rows.map((row) => row.election_year))].sort((a, b) => b - a);
    },
    staleTime: 5 * 60 * 1000,
  });

  // Status vocabulary follows the role filter (candidate vs officeholder truth
  // lives in different tables); legislator roster rows have no editable
  // status, so the filter hides there — matching the backend, which ignores it.
  const statusOptions =
    roleType === 'candidates'
      ? candidateStatuses.data ?? []
      : roleType === 'officeholders'
        ? officeholderStatuses.data ?? []
        : roleType === 'all'
          ? [...new Set([...(candidateStatuses.data ?? []), ...(officeholderStatuses.data ?? [])])]
          : [];
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [personForm, setPersonForm] = useState(EMPTY_PERSON_FORM);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [selectedCandidacies, setSelectedCandidacies] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const fecInputRef = useRef<HTMLInputElement>(null);

  const fecImportMutation = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.post('/candidates/import-federal', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (response) => {
      toast.success(response.data.message ?? 'Federal candidates imported');
      list.refetch();
    },
    onError: (error: any) =>
      toast.error(
        error?.response?.data?.error ??
          error?.response?.data?.message ??
          'Failed to import federal candidates'
      ),
  });

  const addPersonMutation = useMutation({
    mutationFn: () =>
      api.post('/persons', {
        first_name: personForm.first_name.trim(),
        last_name: personForm.last_name.trim(),
        email: personForm.email.trim() || undefined,
        phone: personForm.phone.trim() || undefined,
      }),
    onSuccess: (response) => {
      toast.success('Person created');
      setAddPersonOpen(false);
      setPersonForm(EMPTY_PERSON_FORM);
      navigate(`/admin-next/people/${response.data.data.id}`);
    },
    onError: (error: any) => {
      const existingId = error?.response?.data?.person_id;
      if (error?.response?.status === 409 && existingId) {
        toast.error('A person with this name already exists — opening their record.');
        setAddPersonOpen(false);
        setPersonForm(EMPTY_PERSON_FORM);
        navigate(`/admin-next/people/${existingId}`);
        return;
      }
      toast.error(
        error?.response?.data?.detail ??
          error?.response?.data?.error ??
          error?.response?.data?.errors?.[0]?.msg ??
          'Failed to create person'
      );
    },
  });

  const list = useAdminList<PersonRow>({
    queryKey: 'people-registry',
    endpoint: '/admin/candidates',
    parse: parsePeople,
    filters: {
      registry_type: roleType,
      status: status || undefined,
      office_sought: office || undefined,
      district: districts.length > 0 ? districts : undefined,
      election_year: year.trim() || undefined,
    },
    initialSort: { key: 'name', direction: 'asc' },
    sortParamNames: { sort: 'sortBy', order: 'sortDirection' },
  });

  // Selection only ever refers to rows on screen — prune it whenever the
  // page, filters, or data change so bulk actions never touch unseen rows.
  useEffect(() => {
    setSelectedCandidacies((current) =>
      current.filter((id) => list.rows.some((row) => row.candidate_id === id))
    );
  }, [list.rows]);

  const selectableIds = list.rows
    .filter((row) => row.candidate_id)
    .map((row) => row.candidate_id as string);

  const candidacyStatusMutation = useMutation({
    mutationFn: ({ candidateId, status: statusValue }: { candidateId: string; status: string }) =>
      api.patch(`/admin/candidates/${candidateId}/status`, { status: statusValue }),
    onSuccess: () => {
      toast.success('Candidacy status updated');
      list.refetch();
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.message ?? error?.response?.data?.error ?? 'Failed to update status'),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: (statusValue: string) =>
      api.post('/admin/candidates/bulk-status', {
        candidateIds: selectedCandidacies,
        status: statusValue,
      }),
    onSuccess: (response) => {
      toast.success(`${response.data.updated ?? selectedCandidacies.length} candidacy status(es) updated`);
      setSelectedCandidacies([]);
      setBulkStatus('');
      list.refetch();
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.message ?? error?.response?.data?.error ?? 'Failed to bulk-update status'),
  });

  return (
    <AdminPageShell
      title="People"
      description="Every person in the registry — candidacies, officeholder terms, and legislator history on one record"
    >
      <AdminListToolbar
        search={list.search}
        onSearch={list.setSearch}
        searchPlaceholder="Search by name, office, or district…"
        searchAriaLabel="Search people"
        actions={
          <>
            <input
              ref={fecInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              aria-label="FEC candidate summary CSV"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) fecImportMutation.mutate(file);
                event.target.value = '';
              }}
            />
            <Button variant="outline" size="sm" icon={Upload} loading={fecImportMutation.isLoading}
              title="Import U.S. House/Senate candidates from an FEC Candidate Summary CSV"
              onClick={() => fecInputRef.current?.click()}>
              Import FEC CSV
            </Button>
            <Button variant="outline" size="sm" icon={GitMerge} onClick={() => setDuplicatesOpen(true)}>
              Review Duplicates
            </Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setAddPersonOpen(true)}>
              Add Person
            </Button>
          </>
        }
      >
        <select
          value={roleType}
          onChange={(event) => {
            setRoleType(event.target.value);
            setStatus('');
          }}
          aria-label="Filter by role"
          className={filterSelectClasses}
        >
          <option value="all">All roles</option>
          <option value="candidates">Candidates</option>
          <option value="legislators">Legislators</option>
          <option value="officeholders">Officeholders</option>
        </select>
        {roleType !== 'legislators' && (
          <>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              aria-label="Filter by status"
              className={filterSelectClasses}
            >
              <option value="">All statuses</option>
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <HelpTrigger contentKey="candidate-statuses" size="sm" ariaLabel="What each candidate status means" />
          </>
        )}
        <select
          value={office}
          onChange={(event) => setOffice(event.target.value)}
          aria-label="Filter by office"
          className={filterSelectClasses}
        >
          <option value="">All offices</option>
          {OFFICE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <MultiSelectFilter
          label="Districts"
          fetchOptions={fetchDistrictOptions}
          selectedIds={districts}
          onChange={setDistricts}
        />
        <select
          value={year}
          onChange={(event) => setYear(event.target.value)}
          aria-label="Filter by election cycle"
          className={filterSelectClasses}
        >
          <option value="">All cycles</option>
          {(cycleYears.data ?? []).map((cycleYear) => (
            <option key={cycleYear} value={String(cycleYear)}>
              {cycleYear}
            </option>
          ))}
        </select>
      </AdminListToolbar>

      <BulkActionsBar
        count={selectedCandidacies.length}
        noun="candidate"
        onClear={() => setSelectedCandidacies([])}
      >
        <select
          value={bulkStatus}
          onChange={(event) => setBulkStatus(event.target.value)}
          aria-label="Bulk candidacy status"
          className={filterSelectClasses}
        >
          <option value="">Set status to…</option>
          {(candidateStatuses.data ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          size="sm"
          disabled={bulkStatus === ''}
          loading={bulkStatusMutation.isLoading}
          onClick={() => bulkStatusMutation.mutate(bulkStatus)}
        >
          Apply
        </Button>
      </BulkActionsBar>

      {list.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load people. Please try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={list.isFetching && !list.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell isCheckbox>
                <input
                  type="checkbox"
                  aria-label="Select all candidates on this page"
                  checked={selectableIds.length > 0 && selectableIds.every((id) => selectedCandidacies.includes(id))}
                  onChange={(event) => setSelectedCandidacies(event.target.checked ? selectableIds : [])}
                />
              </StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="name" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Name
              </StandardTableHeaderCell>
              <StandardTableHeaderCell>Roles</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md" sortable sortKey="office" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Office
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" sortable sortKey="district" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                District
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" sortable sortKey="party" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Party
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="xl" sortable sortKey="year" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Latest
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions">Actions</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {list.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : list.rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message={list.search ? `No people match "${list.search}"` : 'No people found'}
                icon={<PeopleIcon className="h-8 w-8" />}
              />
            ) : (
              list.rows.map((row) => (
                <StandardTableRow
                  key={`${row.person_id}-${row.entity_kind}`}
                  onClick={() => navigate(`/admin-next/people/${row.person_id}`)}
                >
                  <StandardTableCell isCheckbox>
                    {row.candidate_id && (
                      <div onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.full_name}`}
                          checked={selectedCandidacies.includes(row.candidate_id)}
                          onChange={() =>
                            setSelectedCandidacies((current) =>
                              current.includes(row.candidate_id as string)
                                ? current.filter((id) => id !== row.candidate_id)
                                : [...current, row.candidate_id as string]
                            )
                          }
                        />
                      </div>
                    )}
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="flex items-center gap-2">
                      {row.photo_url && (
                        <img src={row.photo_url} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                      )}
                      <div>
                        <div className="font-medium text-text-primary dark:text-text-dark-primary">
                          {row.full_name}
                        </div>
                        {(row.possible_duplicate_count ?? 0) > 0 && (
                          <div className="text-xs text-warning-700 dark:text-warning-400">
                            Possible duplicate name ({row.possible_duplicate_count})
                          </div>
                        )}
                      </div>
                    </div>
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="flex flex-wrap gap-1">
                      {row.candidate_id ? (
                        <span
                          onClick={(event) => event.stopPropagation()}
                          className="inline-flex items-center gap-1 rounded-full bg-primary-100 py-0.5 pl-2 pr-1 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:bg-opacity-40 dark:text-primary-300"
                        >
                          Candidate ·
                          <select
                            value={row.candidate_status ?? row.status}
                            onChange={(event) =>
                              candidacyStatusMutation.mutate({
                                candidateId: row.candidate_id as string,
                                status: event.target.value,
                              })
                            }
                            aria-label={`Candidacy status for ${row.full_name}`}
                            className="cursor-pointer rounded bg-transparent text-xs font-medium text-primary-800 outline-none dark:text-primary-300 [&>option]:text-text-primary dark:[&>option]:bg-background-dark-secondary dark:[&>option]:text-text-dark-primary"
                          >
                            {[...new Set([row.candidate_status ?? row.status, ...(candidateStatuses.data ?? [])])].map(
                              (value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              )
                            )}
                          </select>
                        </span>
                      ) : (
                        (row.candidate_status || row.entity_kind === 'candidate' || row.entity_kind === 'both') &&
                        roleChip(
                          'Candidate',
                          row.candidate_status ?? row.status,
                          'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:bg-opacity-40 dark:text-primary-300'
                        )
                      )}
                      {(row.legislator_status || row.entity_kind === 'legislator' || row.entity_kind === 'both') &&
                        roleChip(
                          'Legislator',
                          row.legislator_status,
                          'bg-success-100 text-success-800 dark:bg-success-900 dark:bg-opacity-40 dark:text-success-300'
                        )}
                      {(row.officeholder_status || row.entity_kind === 'officeholder') &&
                        roleChip(
                          'Officeholder',
                          row.officeholder_status,
                          'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:bg-opacity-40 dark:text-warning-300'
                        )}
                    </div>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{row.office_sought || '—'}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{row.district_display ?? row.district ?? '—'}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{formatPartyDisplay(row.party_affiliation) || '—'}</StandardTableCell>
                  <StandardTableCell responsiveHide="xl">
                    {[
                      row.candidate_latest_year ? `C ${row.candidate_latest_year}` : null,
                      row.legislator_latest_session_year ? `L ${row.legislator_latest_session_year}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </StandardTableCell>
                  <StandardTableCell>
                    <div onClick={(event) => event.stopPropagation()}>
                      <Link to={`/admin-next/people/${row.person_id}`}>
                        <Button variant="ghost" size="sm" ariaLabel={`Edit ${row.full_name}`}>
                          Edit
                        </Button>
                      </Link>
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      {!list.isError && (
        <ListPager
          offset={list.offset}
          limit={list.limit}
          total={list.total}
          page={list.page}
          pageCount={list.pageCount}
          hasPrev={list.hasPrev}
          hasNext={list.hasNext}
          onPrev={list.prevPage}
          onNext={list.nextPage}
          noun="people"
        />
      )}

      <AdminModal
        isOpen={addPersonOpen}
        title="Add Person"
        onClose={() => setAddPersonOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setAddPersonOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={personForm.first_name.trim() === '' || personForm.last_name.trim() === ''}
              loading={addPersonMutation.isLoading}
              onClick={() => addPersonMutation.mutate()}
            >
              Create Person
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {(
            [
              { key: 'first_name', label: 'First Name *', type: 'text' },
              { key: 'last_name', label: 'Last Name *', type: 'text' },
              { key: 'email', label: 'Email', type: 'email' },
              { key: 'phone', label: 'Phone', type: 'text' },
            ] as const
          ).map((field) => (
            <div key={field.key}>
              <label
                htmlFor={`add-person-${field.key}`}
                className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary"
              >
                {field.label}
              </label>
              <input
                id={`add-person-${field.key}`}
                type={field.type}
                value={personForm[field.key]}
                onChange={(event) => setPersonForm((current) => ({ ...current, [field.key]: event.target.value }))}
                className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              />
            </div>
          ))}
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            Creates the person record only — add candidacies and officeholder terms from their Roles tab.
          </p>
        </div>
      </AdminModal>

      <DuplicatesReviewModal isOpen={duplicatesOpen} onClose={() => setDuplicatesOpen(false)} />
    </AdminPageShell>
  );
};

export default PeoplePage;
